import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Select,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { toZonedTime } from 'date-fns-tz';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import { FiRefreshCw } from 'react-icons/fi';

import { formatDate } from 'clipsal-cortex-utils/src/formatting/formatting';

import { CustomDatePicker } from '../../../../../common/components/date-picker/CustomDatePicker';
import {
  FORMATTED_TIMEZONES_FOR_SELECT,
  LONG_DATA_TYPE_TO_TITLE_MAP,
  SHORT_DATA_TYPE_TO_TITLE_MAP,
} from '../meter-config-constants';
import { LongPowerDataType, ShortPowerDataType, SiteMeterData } from '../meter-config-types';
import { LongEnergyTable } from './LongEnergyTable';
import { ShortEnergyTable } from './ShortEnergyTable';
import { IntervalType, useMeterChartConfig } from './use-meter-chart-config';

export function MeterConfigurationChart({ selectedMeter }: Readonly<{ selectedMeter: SiteMeterData }>) {
  const {
    seriesData,
    chartOptions,
    selectedInterval,
    selectedShortPowerDataType,
    selectedLongPowerDataType,
    selectedDate,
    hiddenCircuitIndexes,
    timezone,
    latestCommunicatedTime,
    longChartData,
    shortChartData,
    handleToggleLine,
    checkDateBeforeToday,
    refetchLongMetroLogyData,
    isFetchingLongMetroLogyData,
    chartRef,
    setState,
    isShortEnergySelected,
    circuits,
    isLoadingMetrologyData,
    isLoadingSiteCircuits,
    isLoadingAssignments,
  } = useMeterChartConfig(selectedMeter);

  return (
    <Box>
      <Grid templateColumns={'1fr 1fr'} gap={4} maxW={800}>
        <GridItem w="100%">
          <FormControl>
            <FormLabel>Select Interval</FormLabel>
            <Select
              data-testid="energy-type-select"
              value={selectedInterval}
              onChange={(e) => {
                setState((prevState) => ({
                  ...prevState,
                  selectedInterval: e.target.value as IntervalType,
                }));
                // reset zoom on interval change so chart doesn't get blank
                chartRef.current?.chart.zoomOut();
              }}
            >
              <option value="Short Energy">Short Energy</option>
              <option value="Long Energy">Long Energy</option>
            </Select>
          </FormControl>
        </GridItem>
        <GridItem w="100%">
          <FormControl>
            <FormLabel>Select Data Type</FormLabel>
            <Select
              data-testid="data-type-select"
              value={isShortEnergySelected ? selectedShortPowerDataType : selectedLongPowerDataType}
              onChange={
                isShortEnergySelected
                  ? (e) =>
                      setState((prevState) => ({
                        ...prevState,
                        selectedShortPowerDataType: e.target.value as ShortPowerDataType,
                      }))
                  : (e) =>
                      setState((prevState) => ({
                        ...prevState,
                        selectedLongPowerDataType: e.target.value as LongPowerDataType,
                      }))
              }
            >
              {isShortEnergySelected
                ? Object.entries(SHORT_DATA_TYPE_TO_TITLE_MAP).map(([type, label]) => (
                    <option key={type} value={type}>
                      {label}
                    </option>
                  ))
                : Object.entries(LONG_DATA_TYPE_TO_TITLE_MAP).map(([type, label]) => (
                    <option key={type} value={type}>
                      {label}
                    </option>
                  ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>
      <Grid templateColumns={'1fr 1fr'} gap={4} mt={4} maxW={800}>
        <GridItem w="100%">
          <FormControl>
            <FormLabel>Set Timezone</FormLabel>
            <Select
              data-testid="timezone-select"
              value={timezone}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  timezone: e.target.value,
                }))
              }
            >
              {FORMATTED_TIMEZONES_FOR_SELECT.map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem w="100%">
          {isShortEnergySelected ? (
            <Flex w="100%" h="100%">
              <Text textAlign={'center'} mt="auto" mb={2}>
                <strong style={{ fontWeight: 600 }}>Last communicated at</strong> {latestCommunicatedTime}
              </Text>
            </Flex>
          ) : (
            <Flex direction="column">
              <Text fontWeight={600} ml={12} mb={2}>
                Set Effective Date
              </Text>
              <Flex align="center">
                <IconButton
                  data-testid={'prev-date'}
                  size={'md'}
                  rounded={20}
                  mr={2}
                  isDisabled={isFetchingLongMetroLogyData}
                  variant={'ghost'}
                  aria-label="Previous"
                  icon={<ChevronLeftIcon h={5} w={5} />}
                  onClick={() => {
                    const date = new Date(selectedDate.getTime());
                    date.setDate(date.getDate() - 1);

                    setState((prevState) => ({
                      ...prevState,
                      selectedDate: date,
                    }));
                  }}
                />
                <CustomDatePicker
                  maxDate={new Date()}
                  inputProps={{
                    'data-testid': 'meter-config-date-picker',
                    placeholder: 'dd/mm/yyyy',
                    minW: 150,
                  }}
                  disabled={isFetchingLongMetroLogyData}
                  popperPlacement="top-start"
                  dateFormat="dd/MM/yyyy"
                  selected={selectedDate}
                  onChange={(e) => {
                    setState((prevState) => ({
                      ...prevState,
                      selectedDate: new Date((e as Date).getTime()),
                    }));
                  }}
                />
                <IconButton
                  data-testid={'next-date'}
                  size={'md'}
                  rounded={20}
                  mr={2}
                  variant={'ghost'}
                  aria-label="Previous"
                  icon={<ChevronRightIcon h={5} w={5} />}
                  isDisabled={
                    isFetchingLongMetroLogyData ||
                    formatDate(selectedDate) === formatDate(toZonedTime(new Date(), timezone))
                  }
                  onClick={() => {
                    const date = new Date(selectedDate.getTime());

                    // Can't go into the future
                    if (!checkDateBeforeToday(date)) return;

                    date.setDate(date.getDate() + 1);

                    setState((prevState) => ({
                      ...prevState,
                      selectedDate: date,
                    }));
                  }}
                />

                <IconButton
                  data-testid={'refresh-data'}
                  size={'md'}
                  rounded={20}
                  mr={2}
                  aria-label="Previous"
                  isLoading={isFetchingLongMetroLogyData}
                  icon={<FiRefreshCw size={14} />}
                  onClick={refetchLongMetroLogyData}
                />
              </Flex>
            </Flex>
          )}
        </GridItem>
      </Grid>

      <Box style={{ margin: '1rem 0' }} data-testid={`chart-${selectedInterval}`} minH={400}>
        {!isFetchingLongMetroLogyData && !isLoadingMetrologyData && !seriesData.data.some(({ data }) => data.length) ? (
          <Box px={4} py={90} border={'1px solid'} borderColor={'gray.200'} borderRadius={8} textAlign="center">
            There is no data for this device on this day.
          </Box>
        ) : (
          <Skeleton isLoaded={!isLoadingMetrologyData && !isLoadingSiteCircuits}>
            <Chart ref={chartRef} highcharts={Highcharts} options={chartOptions} />
          </Skeleton>
        )}

        <Box mt={4}>
          <Flex mb={2}>
            <Text mr={1} fontWeight={500}>
              Latest Data
            </Text>
            <Text>(polled at {latestCommunicatedTime})</Text>
          </Flex>
          <Skeleton isLoaded={!isLoadingMetrologyData && !isLoadingSiteCircuits && !isLoadingAssignments} minH={200}>
            {isShortEnergySelected ? (
              <ShortEnergyTable
                circuits={circuits}
                onToggleLine={handleToggleLine}
                hiddenIndexes={hiddenCircuitIndexes}
                shortChartData={shortChartData}
              />
            ) : (
              <LongEnergyTable
                circuits={circuits}
                onToggleLine={handleToggleLine}
                hiddenIndexes={hiddenCircuitIndexes}
                longChartData={longChartData}
              />
            )}
          </Skeleton>
        </Box>
      </Box>
    </Box>
  );
}
